import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, useApi, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Loading, Empty, AvailBadge, InfoRow, ConfirmDialog } from "../components/common";
import { Button } from "../components/ui/button";
import { VehicleForm } from "../components/Forms";
import DocumentsPanel from "../components/DocumentsPanel";
import { useAssignmentDelete } from "../components/AssignmentDelete";
import { EditTimeButton, useAssignmentEdit } from "../components/AssignmentEdit";
import { VehicleThumb } from "./Vehicles";
import { eur, fmtDate } from "../lib/format";

function VehicleSummary({ v, isAdmin, onEdit, onDelete }) {
  return (
    <div className="surface overflow-hidden">
      <VehicleThumb v={v} className="h-56" />
      <div className="p-5">
        <div className="eyebrow">{v.type}</div>
        <h1 className="text-2xl font-bold" data-testid="vehicle-name">{v.brand} {v.model}</h1>
        <div className="font-mono text-sm text-muted-foreground">{v.plate}</div>
        <div className="mt-3"><AvailBadge status={v.availability.status} testid="vehicle-availability-badge" /></div>
        <div className="mt-4">
          <InfoRow label="Cantiere attuale" value={v.availability.site_name} />
          <InfoRow label="Revisione" value={fmtDate(v.revision_date)} /><InfoRow label="Assicurazione" value={fmtDate(v.insurance_date)} />
          <InfoRow label="Bollo" value={fmtDate(v.tax_date)} /><InfoRow label="Costo giornaliero" value={eur(v.daily_cost)} /><InfoRow label="Note" value={v.notes} />
        </div>
        {isAdmin && (
          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1" onClick={onEdit} data-testid="vehicle-edit-btn"><Pencil className="h-4 w-4 mr-2" />Modifica</Button>
            <Button variant="outline" className="text-red-400 border-red-900" onClick={onDelete} data-testid="vehicle-delete-btn"><Trash2 className="h-4 w-4" /></Button>
          </div>
        )}
      </div>
    </div>
  );
}

function VehicleAssignments({ items, onDelete, onEdit }) {
  if (!items.length) return <Empty title="Nessuna assegnazione" />;
  return items.map((a) => (
    <div key={a.id} className="flex items-center justify-between gap-3 py-2.5 border-b border-border/60 text-sm" data-testid={`vehicle-assignment-${a.id}`}>
      <Link to={`/cantieri/${a.site_id}`} className="font-medium hover:text-amber-300 flex-1 min-w-0 truncate">{a.site_name}</Link>
      <span className="num text-muted-foreground">{fmtDate(a.start_date)} → {fmtDate(a.end_date)} · {a.days} gg · {a.shift}</span>
      <span className="num text-amber-300">{eur(a.cost)}</span>
      <EditTimeButton onClick={() => onEdit(a)} testid={`vehicle-assignment-time-${a.id}`} />
      <Button size="icon" variant="ghost" className="text-red-400 h-8 w-8" onClick={() => onDelete(a)} data-testid={`vehicle-assignment-delete-${a.id}`} aria-label="Cancella assegnazione"><Trash2 className="h-4 w-4" /></Button>
    </div>
  ));
}

export default function VehicleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const { data: v, loading, reload } = useApi(`/vehicles/${id}`);
  const [edit, setEdit] = useState(false);
  const [del, setDel] = useState(false);
  const assignDelete = useAssignmentDelete(reload);
  const timeEdit = useAssignmentEdit(reload);
  if (loading && !v) return <Loading />;
  if (!v) return <Empty title="Mezzo non trovato" />;
  const remove = async () => {
    try {
      await api.delete(`/vehicles/${v.id}`);
      toast.success("✓ Mezzo eliminato");
      nav("/mezzi");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <button onClick={() => nav(-1)} className="text-sm text-muted-foreground hover:text-white flex items-center gap-1 mb-4" data-testid="back-btn"><ArrowLeft className="h-4 w-4" />Indietro</button>
      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        <VehicleSummary v={v} isAdmin={isAdmin} onEdit={() => setEdit(true)} onDelete={() => setDel(true)} />
        <div className="space-y-6">
          <section className="surface p-5">
            <h2 className="text-lg font-semibold mb-3">Utilizzi e assegnazioni</h2>
            <VehicleAssignments items={v.assignments} onDelete={assignDelete.ask} onEdit={timeEdit.ask} />
          </section>
          <section className="surface p-5">
            <h2 className="text-lg font-semibold mb-3">Documenti (libretti, assicurazioni, revisioni)</h2>
            <DocumentsPanel entityType="vehicle" entityId={v.id} canEdit={isAdmin} />
          </section>
        </div>
      </div>
      {assignDelete.dialog}
      {timeEdit.dialog}
      <VehicleForm open={edit} onOpenChange={setEdit} vehicle={v} onSaved={reload} />
      <ConfirmDialog open={del} onOpenChange={setDel} title={`Eliminare ${v.brand} ${v.model}?`} description="Verranno eliminate anche le assegnazioni del mezzo." onConfirm={remove} />
    </div>
  );
}
